/* ============================================================
   ORION QUICK ADD

   A cart upsell used to add whatever variant happened to be first
   in the list. For a product like the 75-serving bulk bundle —
   three options, one flavour each — that meant a tap silently
   chose a combination for the customer.

   This opens a picker instead, and handles the two shapes Orion
   sells:
     • mix-and-match — 2+ options whose value lists are identical
       (pick 3 bags from the same 3 flavours). Rendered as slots,
       resolved as an UNORDERED multiset, because "Cookies,
       Cinnamon, Cookies" and "Cookies, Cookies, Cinnamon" are the
       same box but different Shopify variants.
     • ordinary variants — one picker per option.

   Products with a single variant never open it; they add straight
   away, as before.
   ============================================================ */
(() => {
  if (window.orionQuickAdd) return;

  const cache = new Map();
  let dialog;
  let state = null;

  const money = (c) =>
    '$' + (c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const str = (k, fallback) => (window.orionQuickAddStrings || {})[k] || fallback;

  /** Distinct values per option index, in variant order. */
  function optionValues(product) {
    const count = (product.variants[0]?.options || []).length;
    const cols = Array.from({ length: count }, () => []);
    product.variants.forEach((v) => {
      (v.options || []).forEach((val, i) => {
        if (!cols[i].includes(val)) cols[i].push(val);
      });
    });
    return cols;
  }

  function isMixAndMatch(cols) {
    if (cols.length < 2) return false;
    const first = cols[0].slice().sort().join(' ');
    return cols.every((c) => c.slice().sort().join(' ') === first);
  }

  /** Order-independent key, so a box of the same bags matches whatever the
      order the customer tapped them in. */
  const key = (arr) => arr.slice().sort().join(' ');

  function findVariant(product, picks) {
    const wanted = key(picks);
    return product.variants.find((v) => key(v.options || []) === wanted) || null;
  }

  function optionNames(product) {
    return (product.options || []).map((o) => (typeof o === 'string' ? o : o.name));
  }

  function build() {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'o-qa__dialog';
    dialog.innerHTML =
      '<div class="o-qa__sheet">' +
      '<header class="o-qa__head">' +
      '<div><p class="o-qa__eyebrow" data-qa-eyebrow></p><h2 class="o-display o-qa__title" data-qa-title></h2></div>' +
      '<button type="button" class="o-qa__close" data-qa-close aria-label="' + str('close', 'Close') + '">&times;</button>' +
      '</header>' +
      '<div class="o-qa__body" data-qa-body></div>' +
      '<p class="o-qa__error" data-qa-error hidden role="alert"></p>' +
      '<button type="button" class="o-qa__cta" data-qa-cta></button>' +
      '</div>';
    document.body.appendChild(dialog);

    dialog.querySelector('[data-qa-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });
    dialog.querySelector('[data-qa-cta]').addEventListener('click', commit);
    return dialog;
  }

  function paint() {
    const { product, cols, mix, picks, slot } = state;
    const body = dialog.querySelector('[data-qa-body]');
    const cta = dialog.querySelector('[data-qa-cta]');
    body.replaceChildren();

    if (mix) {
      const slots = document.createElement('div');
      slots.className = 'o-qa__slots';
      picks.forEach((val, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'o-qa__slot' + (val ? ' is-filled' : '') + (i === slot ? ' is-active' : '');
        b.textContent = val || str('slotEmpty', 'Choose');
        b.addEventListener('click', () => {
          state.slot = i;
          state.picks[i] = null;
          paint();
        });
        slots.appendChild(b);
      });
      body.appendChild(slots);

      const hint = document.createElement('p');
      hint.className = 'o-qa__hint';
      const left = picks.filter((p) => !p).length;
      hint.textContent = left
        ? str('pickMore', 'Pick {n} more').replace('{n}', left)
        : str('pickDone', 'Your box is full. Tap a bag to change it.');
      body.appendChild(hint);

      const grid = document.createElement('div');
      grid.className = 'o-qa__choices';
      cols[0].forEach((val) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'o-qa__choice';
        b.textContent = val;
        b.addEventListener('click', () => {
          const target = state.picks.findIndex((p, i) => (i === state.slot ? !p : false));
          const idx = target !== -1 ? target : state.picks.findIndex((p) => !p);
          if (idx === -1) return;
          state.picks[idx] = val;
          state.slot = state.picks.findIndex((p) => !p);
          paint();
        });
        grid.appendChild(b);
      });
      body.appendChild(grid);
    } else {
      const names = optionNames(product);
      cols.forEach((values, i) => {
        const group = document.createElement('div');
        group.className = 'o-qa__group';
        const label = document.createElement('p');
        label.className = 'o-qa__label';
        label.textContent = names[i] || str('option', 'Option');
        group.appendChild(label);
        const row = document.createElement('div');
        row.className = 'o-qa__choices';
        values.forEach((val) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'o-qa__choice' + (picks[i] === val ? ' is-on' : '');
          b.textContent = val;
          b.addEventListener('click', () => {
            state.picks[i] = val;
            paint();
          });
          row.appendChild(b);
        });
        group.appendChild(row);
        body.appendChild(group);
      });
    }

    const complete = state.picks.every(Boolean);
    const variant = complete ? findVariant(product, state.picks) : null;
    state.variant = variant;

    if (!complete) {
      cta.disabled = true;
      cta.textContent = str('pickAll', 'Choose your options');
    } else if (!variant || !variant.available) {
      cta.disabled = true;
      cta.textContent = str('unavailable', 'That combination is sold out');
    } else {
      cta.disabled = false;
      cta.textContent = str('add', 'Add to cart') + ' · ' + money(variant.price);
    }
  }

  async function commit() {
    if (!state || !state.variant) return;
    const cta = dialog.querySelector('[data-qa-cta]');
    const err = dialog.querySelector('[data-qa-error]');
    cta.disabled = true;
    cta.classList.add('is-loading');
    err.hidden = true;
    try {
      const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [(() => {
            const item = { id: state.variant.id, quantity: 1 };
            if (state.sellingPlan) item.selling_plan = state.sellingPlan;
            return item;
          })()],
        }),
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.description || 'add failed');
      dialog.close();
      document.dispatchEvent(new CustomEvent('orion:cart:added', { detail: { sections: payload.sections } }));
    } catch (e) {
      err.textContent = str('addFail', 'Could not add that. Please try again.');
      err.hidden = false;
    } finally {
      cta.classList.remove('is-loading');
      paint();
    }
  }

  async function load(handle) {
    if (cache.has(handle)) return cache.get(handle);
    const res = await fetch(window.Shopify.routes.root + 'products/' + handle + '.js');
    if (!res.ok) throw new Error('product fetch failed');
    const p = await res.json();
    cache.set(handle, p);
    return p;
  }

  /**
   * Open the picker for a product, or add it directly when there is nothing
   * to choose. Returns true if the picker opened.
   */
  window.orionQuickAdd = async function orionQuickAdd(handle, opts) {
    let product;
    try {
      product = await load(handle);
    } catch (e) {
      return false;
    }

    const cols = optionValues(product);
    const meaningful = cols.filter((c) => c.length > 1);
    // one variant, or a single "Default Title" option: nothing worth asking
    if (product.variants.length < 2 || meaningful.length === 0) return false;

    const mix = isMixAndMatch(cols);
    build();
    state = {
      product,
      cols,
      mix,
      picks: cols.map(() => null),
      slot: 0,
      variant: null,
      // the 3-Month Supply card passes the bundle's selling plan through, so
      // the picked variant is added as a subscription, not a one-off
      sellingPlan: (opts && opts.sellingPlan) || null,
    };

    dialog.querySelector('[data-qa-eyebrow]').textContent = mix
      ? str('mixEyebrow', 'Build your box')
      : str('pickEyebrow', 'Choose your options');
    dialog.querySelector('[data-qa-title]').textContent = product.title;
    dialog.querySelector('[data-qa-error]').hidden = true;

    paint();
    if (!dialog.open) dialog.showModal();
    return true;
  };
})();
